/**
 * Иконки — сетка 16, обводка 1.6, ровно тот набор, что нарисован в утверждённом дизайне
 * (deliverables/ui-design-v1/build/lib.mjs). Перенесены как React-узлы, а не как строки HTML:
 * dangerouslySetInnerHTML в этом репозитории запрещён линтом, и не зря — в фазе 2 текст
 * придёт от модели, обработавшей недоверенный HTML из открытого интернета.
 */

import type { JSX, SVGProps } from 'react';

type IconProps = Omit<SVGProps<SVGSVGElement>, 'children'>;

const Svg = ({ children, ...rest }: SVGProps<SVGSVGElement>): JSX.Element => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...rest}
  >
    {children}
  </svg>
);

export const IconCheck = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M3 8.5l3.5 3.5L13 5" />
  </Svg>
);

export const IconWarn = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M8 2.4L14.4 13.2H1.6Z" />
    <path d="M8 6.6v3" />
    <circle cx="8" cy="11.5" r="0.5" fill="currentColor" stroke="none" />
  </Svg>
);

export const IconInfo = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <circle cx="8" cy="8" r="6.2" />
    <path d="M8 7.4v3.4" />
    <circle cx="8" cy="5.2" r="0.6" fill="currentColor" stroke="none" />
  </Svg>
);

export const IconSun = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <circle cx="8" cy="8" r="3" />
    <path d="M8 1.5v1.8M8 12.7v1.8M1.5 8h1.8M12.7 8h1.8M3.4 3.4l1.3 1.3M11.3 11.3l1.3 1.3M12.6 3.4l-1.3 1.3M4.7 11.3l-1.3 1.3" />
  </Svg>
);

export const IconMoon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M13.2 9.8A5.6 5.6 0 116.2 2.8a4.5 4.5 0 007 7z" />
  </Svg>
);

export const IconLink = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M6.6 9.4l2.8-2.8" />
    <path d="M7.5 4.7l1.4-1.4a2.6 2.6 0 013.8 3.8l-1.4 1.4" />
    <path d="M8.5 11.3l-1.4 1.4a2.6 2.6 0 01-3.8-3.8l1.4-1.4" />
  </Svg>
);

export const IconCode = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M5.4 4.6L2 8l3.4 3.4M10.6 4.6L14 8l-3.4 3.4" />
  </Svg>
);

export const IconQuote = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M4 3.5v9" />
    <path d="M7.5 5.5H13M7.5 8H13M7.5 10.5h3.8" />
  </Svg>
);

export const IconEyeOff = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M2 8s2.3-4.2 6-4.2S14 8 14 8s-2.3 4.2-6 4.2S2 8 2 8z" />
    <circle cx="8" cy="8" r="1.8" />
    <path d="M3 13L13 3" />
  </Svg>
);

export const IconEye = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M1.6 8S4 3.8 8 3.8 14.4 8 14.4 8 12 12.2 8 12.2 1.6 8 1.6 8z" />
    <circle cx="8" cy="8" r="1.9" />
  </Svg>
);

export const IconImage = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <rect x="2" y="3" width="12" height="10" rx="2" />
    <circle cx="5.4" cy="6.4" r="1.1" />
    <path d="M2 11l3.4-3.4 2.6 2.6L11 7.4l3 3.1" />
  </Svg>
);

/** Файл — полоса «Файл» экрана черновика (PDF внизу поста, партия 19.09.2026). */
export const IconFile = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M4.5 1.8h4.7l3.3 3.3v8a1.3 1.3 0 01-1.3 1.3h-6.7a1.3 1.3 0 01-1.3-1.3V3.1a1.3 1.3 0 011.3-1.3z" />
    <path d="M9 2v3.3h3.3" />
  </Svg>
);

export const IconClock = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <circle cx="8" cy="8" r="6.2" />
    <path d="M8 4.6V8l2.4 1.5" />
  </Svg>
);

export const IconExternal = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M6.5 3.5H4a1.5 1.5 0 00-1.5 1.5v7A1.5 1.5 0 004 13.5h7a1.5 1.5 0 001.5-1.5V9.5" />
    <path d="M9.5 2.5H13.5V6.5" />
    <path d="M13.5 2.5L7.5 8.5" />
  </Svg>
);

export const IconRefresh = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M13.4 8A5.4 5.4 0 113.9 4.4" />
    <path d="M3.6 1.8v2.8h2.8" />
  </Svg>
);

export const IconPlus = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M8 3v10M3 8h10" />
  </Svg>
);

export const IconChevronDown = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M4 6.3L8 10.3l4-4" />
  </Svg>
);

export const IconChevronRight = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M6.3 4L10.3 8l-4 4" />
  </Svg>
);

export const IconX = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M4 4l8 8M12 4l-8 8" />
  </Svg>
);

export const IconCalendar = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <rect x="2" y="3.4" width="12" height="10.6" rx="2" />
    <path d="M2 7h12M5.4 1.8v3.2M10.6 1.8v3.2" />
  </Svg>
);

export const IconThumbUp = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M4.6 7.4v6.1H3a.9.9 0 01-.9-.9V8.3a.9.9 0 01.9-.9h1.6zM4.6 7.4l2.5-4.5a1.35 1.35 0 012.55.65v2.35h2.9a1.5 1.5 0 011.48 1.74l-.68 4.1a1.5 1.5 0 01-1.48 1.26H4.6" />
  </Svg>
);

export const IconKey = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <circle cx="5" cy="8" r="3.2" />
    <path d="M8.2 8H14M12 8v2.4M9.8 8v1.6" />
  </Svg>
);

export const IconSend = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M14 2L7.3 8.7" />
    <path d="M14 2l-4.2 12-2.5-5.3L2 6.2z" />
  </Svg>
);

export const IconUser = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <circle cx="8" cy="5.4" r="2.9" />
    <path d="M2.6 13.6a5.4 5.4 0 0110.8 0" />
  </Svg>
);

export const IconShield = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M8 1.8l5 1.9v3.7c0 3.3-2.1 5.7-5 6.8-2.9-1.1-5-3.5-5-6.8V3.7z" />
    <path d="M5.8 8l1.6 1.6 2.8-2.8" />
  </Svg>
);

export const IconUndo = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M6.5 3.5L3 7l3.5 3.5" />
    <path d="M3 7h6.2a3.8 3.8 0 013.8 3.8v.7" />
  </Svg>
);

/** Радар — «Прогон мониторинга» в шапке (партия 19.09.2026). */
export const IconRadar = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <circle cx="8" cy="8" r="6.2" />
    <path d="M8 4.6a3.4 3.4 0 1 0 3.4 3.4" />
    <path d="M8 8l4.1-4.1" />
    <circle cx="8" cy="8" r="0.9" fill="currentColor" stroke="none" />
  </Svg>
);

/**
 * Ползунки — «Настройки» на телефоне, где подписи органа уже не помещаются (лестница шапки,
 * `WriterModelSwitch.module.css`). Ползунки, а не шестерёнка: на сетке 16 шестерёнка при
 * обводке 1.6 слипается в пятно.
 */
export const IconSliders = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M2.5 4.8h7.2M12.9 4.8h.6M2.5 11.2h.6M6.3 11.2h7.2" />
    <circle cx="11.3" cy="4.8" r="1.6" />
    <circle cx="4.7" cy="11.2" r="1.6" />
  </Svg>
);

export const IconSearch = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <circle cx="7" cy="7" r="4.8" />
    <path d="M10.6 10.6L14 14" />
  </Svg>
);

/** Знак GeoBoost — сетка 5×5, размер и насыщенность растут по диагонали (брендбук §4).
 *  Перекрашивать знак запрещено, поэтому цвет здесь литералом, а не через currentColor.
 *
 *  Тот же знак готовыми файлами лежит в `public/favicon.svg` и `public/favicon-32.png` —
 *  иконка вкладки браузера подключена в `index.html`. Разметку в браузер отдаёт vite, а не
 *  React, поэтому эту функцию там не переиспользовать: правишь геометрию здесь — перегенерируй
 *  оба файла по тем же формулам. */
export const Logo = ({ size = 24 }: { size?: number }): JSX.Element => {
  const dots: JSX.Element[] = [];
  for (let r = 0; r < 5; r += 1) {
    for (let c = 0; c < 5; c += 1) {
      const k = (r + c) / 8;
      dots.push(
        <circle
          key={`${String(r)}-${String(c)}`}
          cx={3 + c * 4.5}
          cy={3 + r * 4.5}
          r={(0.9 + k * 1.5).toFixed(2)}
          fill="#4B2EDB"
          opacity={(0.22 + k * 0.78).toFixed(2)}
        />,
      );
    }
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      {dots}
    </svg>
  );
};
